import { useEffect, useState } from 'react';

/**
 * Hook to preload images for better performance
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @param {boolean} shouldPreload - Whether to start preloading
 * @returns {Object} - { loaded, progress }
 */
export const useImagePreloader = (imageUrls = [], shouldPreload = true) => {
  const [loadedImages, setLoadedImages] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!shouldPreload || !imageUrls.length) {
      setIsComplete(true);
      return;
    }

    let isMounted = true;
    let loadedCount = 0;

    const preloadImage = (url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          if (isMounted) {
            loadedCount++;
            setLoadedImages(loadedCount);
            
            if (loadedCount === imageUrls.length) {
              setIsComplete(true);
            }
          }
          resolve(url);
        };
        
        img.onerror = () => {
          console.warn(`Failed to preload image: ${url}`);
          if (isMounted) {
            loadedCount++;
            setLoadedImages(loadedCount);
            
            if (loadedCount === imageUrls.length) {
              setIsComplete(true);
            }
          }
          resolve(url); // Resolve anyway to not block other images
        };
        
        img.src = url;
      });
    };

    // Preload all images
    Promise.all(imageUrls.map(preloadImage)).then(() => {
      if (isMounted) {
        setIsComplete(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [imageUrls, shouldPreload]);

  const progress = imageUrls.length > 0 ? (loadedImages / imageUrls.length) * 100 : 100;

  return {
    loaded: isComplete,
    progress: Math.round(progress),
    loadedCount: loadedImages,
    totalCount: imageUrls.length
  };
};

/**
 * Preload critical images immediately
 * @param {Array<string>} imageUrls 
 */
export const preloadImagesImmediate = (imageUrls) => {
  imageUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  });
};
